import { styled, useTheme } from "@mui/material/styles"
import { BarPlot } from "@mui/x-charts/BarChart"
import { ChartsGrid } from "@mui/x-charts/ChartsGrid"
import { ChartsTooltip } from "@mui/x-charts/ChartsTooltip"
import { ChartsXAxis } from "@mui/x-charts/ChartsXAxis"
import { ChartsYAxis } from "@mui/x-charts/ChartsYAxis"
import { ResponsiveChartContainer } from "@mui/x-charts/ResponsiveChartContainer"
import {
  unstable_useBarSeries,
  useXAxis,
  useXScale,
  useYScale,
} from "@mui/x-charts/hooks"

import { formatCurrency } from "../../../../../common/utils"
import ChartCard from "./ChartCard"

export default function ProductionOverTimeChart({ salesVolumeSidesChartData }) {
  const theme = useTheme()
  const height = 220

  // we're using listing data as reference, though it may have less data than selling
  const listingData =
    salesVolumeSidesChartData[PRODUCTION_SIDES_DATA_KEYS.listing]
  const sellingDataMap = Object.fromEntries(
    salesVolumeSidesChartData[PRODUCTION_SIDES_DATA_KEYS.selling].map(
      ({ year, value }) => [year, value]
    )
  )
  const dataset = listingData.map(({ year, value }) => ({
    year,
    listing: value,
    selling: sellingDataMap[year],
  }))

  return (
    <ChartCard
      title="Production Over Time"
      legendLabel1="List-side"
      legendLabel2="Buy-side"
      chartHeight={height}
    >
      <ResponsiveChartContainer
        dataset={dataset}
        height={height}
        xAxis={[
          {
            dataKey: "year",
            scaleType: "band",
            categoryGapRatio: CATEGORY_GAP_RATIO,
            tickLabelStyle: {
              fontSize: 12,
              fill: theme.palette.text.secondary,
            },
            valueFormatter: (value) => value.toString(),
          },
        ]}
        yAxis={[
          {
            tickLabelStyle: {
              fontSize: 12,
              fill: theme.palette.text.secondary,
            },
            valueFormatter: formatCurrency,
          },
        ]}
        series={[
          {
            id: PRODUCTION_SIDES_DATA_KEYS.listing,
            type: "bar",
            label: "Listing",
            dataKey: PRODUCTION_SIDES_DATA_KEYS.listing,
            stack: "total",
            color: "#9525D6",
            borderRadius: 2,
            valueFormatter: formatCurrency,
          },
          {
            id: PRODUCTION_SIDES_DATA_KEYS.selling,
            type: "bar",
            label: "Buying",
            dataKey: PRODUCTION_SIDES_DATA_KEYS.selling,
            stack: "total",
            color: "#D386FF",
            borderRadius: 2,
            valueFormatter: formatCurrency,
          },
        ]}
        slotProps={{
          legend: { hidden: true },
          animation: {
            duration: 700,
            easing: "ease-out",
          },
        }}
        margin={{ top: 20, right: 20, bottom: 20, left: 45 }}
        sx={{
          "& .MuiChartsAxis-line": {
            stroke: theme.palette.divider,
            strokeWidth: 1,
          },
          "& .MuiChartsAxis-tickLabel": {
            fill: theme.palette.text.secondary,
          },
          "& .MuiBarElement-root": {
            strokeWidth: 0,
          },
        }}
      >
        <BarPlot />
        <ChartsXAxis />
        <ChartsYAxis />
        <ChartsGrid horizontal />
        <ChartsTooltip />
        <BarDirectionIndicator />
      </ResponsiveChartContainer>
    </ChartCard>
  )
}

function BarDirectionIndicator() {
  const barSeries = unstable_useBarSeries()
  const xAxis = useXAxis()
  const xScale = useXScale()
  const yScale = useYScale()

  if (!barSeries) {
    return null
  }

  const listingSeriesData =
    barSeries.series[PRODUCTION_SIDES_DATA_KEYS.listing].data
  const sellingSeriesData =
    barSeries.series[PRODUCTION_SIDES_DATA_KEYS.selling].data
  if (listingSeriesData.length === 0 || sellingSeriesData.length === 0) {
    return null
  }
  const firstYear = xAxis.data[0]
  const nextYear = xAxis.data[1]
  const yearWidth = xScale(nextYear) - xScale(firstYear)
  const listingSellingTotals = listingSeriesData.map(
    (listing, i) => listing + sellingSeriesData[i]
  )
  const maxYValue = Math.max(...listingSellingTotals)
  const textTopMargin = maxYValue / 15
  const upOrDownPercentages = listingSellingTotals.map(
    (listingSellingTotal, i) => {
      let upOrDownPercentage = 0
      let xPos, yPos
      if (i > 0) {
        const prevYearTotal = listingSellingTotals[i - 1]
        upOrDownPercentage =
          (listingSellingTotal - prevYearTotal) / prevYearTotal
        xPos =
          xScale(xAxis.data[i]) +
          yearWidth / 2 -
          yearWidth * (CATEGORY_GAP_RATIO / 2)
        yPos = yScale(listingSellingTotal + textTopMargin)
      }
      return {
        percentage: Math.round(100 * upOrDownPercentage),
        xPos,
        yPos,
      }
    }
  )
  return upOrDownPercentages.map(
    (data, i) =>
      i > 0 && (
        <PercentageText
          key={i}
          x={data.xPos}
          y={data.yPos}
          percentage={data.percentage}
        >
          {data.percentage >= 0 ? "+" : ""}
          {data.percentage.toLocaleString()}%
        </PercentageText>
      )
  )
}

const PercentageText = styled("text", {
  shouldForwardProp: (prop) => prop !== "percentage",
})(({ theme, percentage }) => ({
  fill:
    percentage > 0
      ? theme.palette.success.main
      : percentage < 0
      ? theme.palette.error.main
      : "black",
  fontSize: 12,
  fontWeight: 600,
  dominantBaseline: "middle",
  textAnchor: "middle",
}))

const PRODUCTION_SIDES_DATA_KEYS = {
  listing: "listing",
  selling: "selling",
}

const CATEGORY_GAP_RATIO = 0.2
